import React, { useMemo, useState } from "react";
import countryList from "react-select-country-list";
import TranslationWrapper, { getTranslation } from "../../../components/TranslationWrapper/TranslationWrapper.tsx";
import { GridLegacy, MenuItem } from "@mui/material";
import CardBody from "../../../components/Card/CardBody";
import Button from "../../../components/CustomButtons/Button";
import Card from "../../../components/Card/Card";
import {
  apiProviders,
  apiProviderSupportsFeature,
  dispatchQueue,
  priceModes,
  runningRelease,
  salesareaTypes,
  talenV5,
  timeZones,
} from "../../../variables/app";
import { convertArrayToSelectOptions, convertObjectToSelectOptions } from "../../../templateHelpers";
import FormSelectTC from "../../../components/Forms/FormSelectTC";
import FormSwitchTC from "../../../components/Forms/FormSwitchTC";
import FormAsyncTC from "../../../components/Forms/FormAsyncTC";
import { useFormContext, useWatch } from "react-hook-form";
import { _get } from "../../../http";
import FormFieldTwoColumns from "../../../components/Forms/Layouts/FormFieldTwoColumns";
import FormTextTC from "../../../components/Forms/FormTextTC";
import FormAutocompleteTC from "../../../components/Forms/FormAutocompleteTC";
import { useFetchSalesAreaPriceLinesQuery } from "../../../../../Domains/PriceLines/api/priceLineApi";
import { useAppSelector } from "../../../store";
import { selectSalesAreaId } from "../../../../../Domains/SalesAreas/selectors/selectSalesAreaId";
import currencyData from "currency-codes/data";
import getSymbolFromCurrency from "currency-symbol-map";

const countryOptions = convertArrayToSelectOptions(countryList().getData());
const talenOptions = convertObjectToSelectOptions(talenV5);
const priceModeOptions = convertArrayToSelectOptions(priceModes);
const runningReleaseOptions = convertArrayToSelectOptions(runningRelease);
const dispatchQueueOptions = convertArrayToSelectOptions(dispatchQueue);
const salesareaTypesOptions = convertArrayToSelectOptions(salesareaTypes);

function Algemeen() {
  const { vestigingId, menukaartVestigingId, reloadDataFromServer } = useFormContext();
  const [valueCountry, setValueCountry] = useState("NL");

  const arrangementsEnabled = useWatch({ name: "use_arrangements" });
  const apiProvidersOptions = convertArrayToSelectOptions(
    apiProviders,
    undefined,
    apiProviders.map(({ value }) => {
      const disabled = arrangementsEnabled && !apiProviderSupportsFeature(value, "arrangements");
      return {
        disabled,
        label: (
          <div
            style={{
              display: "grid",
              width: "500px",
              gridTemplateColumns: "128px 1fr",
            }}
          >
            <span>{value}</span>
            {disabled ? <span style={{ justifySelf: "end" }}>Not available for arrangements</span> : null}
          </div>
        ),
      };
    })
  );

  const changeHandlerCountry = (value) => {
    setValueCountry(value);
  };

  const currencyOptions = useMemo(() => {
    const sortOrder = { EUR: 0, GBP: 1, CHF: 2, ANG: 3, USD: 4, NOK: 5, SEK: 6, DKK: 7 };
    return currencyData
      .filter(
        (currency) =>
          currency.code !== "XTS" &&
          currency.code !== "XXX" &&
          currency.code !== "XBA" &&
          currency.code !== "XBB" &&
          currency.code !== "XBC" &&
          currency.code !== "XBD"
      )
      .sort((a, b) => {
        return (sortOrder[a.code] ?? 999) - (sortOrder[b.code] ?? 999);
      })
      .map((currency) => ({
        value: currency.code,
        label:
          (getSymbolFromCurrency(currency.code) ?? "") +
          " " +
          getTranslation(`global.currency.${currency.code}`, `${currency.currency}`) +
          ` (${currency.code})`,
      }));
  }, []);

  const timeZoneOptions = useMemo(() => {
    return timeZones.map((timeZone) => ({ value: timeZone, label: timeZone }));
  }, []);

  const search = () => {
    _get("core/vestigingen/" + vestigingId + "/searchlatlng", null, {
      showToastError: true,
      showToastSuccess: true,
    }).then((response) => {
      if (response.data.data.success) {
        reloadDataFromServer();
      }
    });
  };

  const goLive = () => {
    _get("core/vestigingen/" + vestigingId + "/golive", null, {
      showToastError: true,
      showToastSuccess: true,
    }).then((response) => {
      if (response.data.data.success) {
        reloadDataFromServer();
      }
    });
  };
  const salesAreaId = useAppSelector(selectSalesAreaId);
  const { data } = useFetchSalesAreaPriceLinesQuery(
    { salesAreaId: salesAreaId ?? 0, pagination: { page: 1, perPage: 1000 } },
    { skip: salesAreaId == null }
  );

  const use_sales_area_price_line_id = useWatch({ name: "custom_data.use_sales_area_price_line_id" });
  return (
    <div style={{ height: "100%", width: "100%", overflow: "hidden" }}>
      <GridLegacy container spacing={1}>
        <GridLegacy item lg={6} xs={12}>
          <GridLegacy containe spacing={1}>
            <GridLegacy alignContent={"flex-start"} item xs={12}>
              <Card>
                <CardBody style={{ display: "flex", flexWrap: "wrap" }}>
                  <GridLegacy item xs={12}>
                    <h3>
                      <TranslationWrapper tKey={"Sales area"} />
                    </h3>
                  </GridLegacy>
                  <FormAsyncTC
                    async={{
                      url: "core/venue/search?fields=id as value, name as label&per_page=50",
                      searchField: "name",
                    }}
                    name={"venue_id"}
                    nullable={true}
                    tKey={"Venue"}
                  />
                  <FormSelectTC
                    name={"salesarea_type"}
                    options={salesareaTypesOptions}
                    tDefault={"Product solution"}
                    tKey={"salesarea.type"}
                  />
                  <FormSelectTC
                    name={"apiProvider"}
                    options={apiProvidersOptions}
                    tDefault={"API"}
                    tKey={"vestigingen.form.algemeen.label.apiProvider"}
                  />
                  <FormSelectTC
                    name={"priceMode"}
                    options={priceModeOptions}
                    tDefault={"Price mode"}
                    tKey={"vestigingen.form.algemeen.label.priceMode"}
                  />

                  {(data && data.data.length > 0) || use_sales_area_price_line_id ? (
                    <FormSelectTC
                      name={"custom_data.use_sales_area_price_line_id"}
                      options={[
                        <MenuItem key={"none"} value={""}>
                          None
                        </MenuItem>,
                        ...(data?.data.map((priceLine) => (
                          <MenuItem key={priceLine.id} value={priceLine.id}>
                            {priceLine.title}
                          </MenuItem>
                        )) ?? []),
                      ]}
                      tDefault={"Use a price line"}
                      tKey={"custom_data.use_sales_area_price_line_id"}
                    />
                  ) : null}
                  <FormSelectTC
                    name={"forcelang"}
                    options={talenOptions}
                    tDefault={"Applicatietaal"}
                    tKey={"vestigingen.form.algemeen.label.forcelang"}
                  />
                  <FormSelectTC
                    name={"mainLanguage"}
                    options={talenOptions}
                    tDefault={"Taal kassa/printer"}
                    tKey={"vestigingen.form.algemeen.label.mainLanguage"}
                  />
                  <FormAutocompleteTC
                    name={"valuta"}
                    options={currencyOptions}
                    tDefault={"Valuta"}
                    tKey={"vestigingen.form.overige.label.valuta"}
                  />
                  <FormSelectTC
                    name={"custom_data.currencyMinFractionDigits"}
                    options={[
                      <MenuItem key={"-"} value={""}>
                        -
                      </MenuItem>,
                      <MenuItem key={0} value={0}>
                        0
                      </MenuItem>,
                      <MenuItem key={1} value={1}>
                        1
                      </MenuItem>,
                      <MenuItem key={2} value={2}>
                        2
                      </MenuItem>,
                      <MenuItem key={3} value={3}>
                        3
                      </MenuItem>,
                    ]}
                    tDefault={"Minimum Fraction digits (currency)"}
                    tKey={"vestigingen.form.overige.label.valutaMinFractionDigits"}
                  />
                  <FormSwitchTC
                    name={"custom_data.currencySymbolShow"}
                    tDefault={"Display currency in app"}
                    tKey={"vestigingen.form.overige.label.valutaSymbol"}
                    defaultValue={true}
                  />
                  <FormAutocompleteTC
                    name={"timezone"}
                    options={timeZoneOptions}
                    tDefault={"Timezone"}
                    tKey={"vestigingen.form.algemeen.label.timezone"}
                  />
                  <FormTextTC name={"nlFlag"} tDefault={"Flag"} tKey={"vestigingen.form.overige.label.nlFlag"} />
                  <FormTextTC
                    name={"defaultSMSLang"}
                    tDefault={"SMS country"}
                    tKey={"vestigingen.form.algemeen.label.defaultSMSLang"}
                  />
                </CardBody>
              </Card>
            </GridLegacy>
            <GridLegacy alignContent={"flex-start"} item xs={12}>
              <Card>
                <CardBody>
                  <GridLegacy item xs={12}>
                    <h3>
                      <TranslationWrapper
                        tDefault={"Salesarea Connections"}
                        tKey={"vestigingen.form.algemeen.headers.connections"}
                      />
                    </h3>
                  </GridLegacy>
                  <FormAsyncTC
                    async={{
                      url: "core/vestigingen/search?fields=id as value, systeemNaam as label&per_page=50&sort_field=systeemNaam",
                      searchField: "systeemNaam",
                    }}
                    name={"parent_id"}
                    nullable={true}
                    tDefault={"Hoofd vestiging"}
                    tKey={"vestigingen.form.algemeen.label.parent_id"}
                  />
                  <FormAsyncTC
                    async={{
                      url: "core/vestigingen/search?fields=id as value, systeemNaam as label&per_page=50&sort_field=systeemNaam",
                      searchField: "systeemNaam",
                    }}
                    name={"menukaartVestiging"}
                    nullable={true}
                    tDefault={"Menukaart"}
                    tKey={"vestigingen.form.algemeen.label.menukaartVestiging"}
                  />
                  <FormAsyncTC
                    async={{
                      url: "core/vestigingen/search?fields=id as value, systeemNaam as label&per_page=50&sort_field=systeemNaam",
                      searchField: "systeemNaam",
                    }}
                    name={"styling_vestiging_id"}
                    nullable={true}
                    tDefault={"Use design of other salesarea"}
                    tKey={"vestigingen.form.algemeen.label.styling_vestiging_id"}
                  />
                  <FormAsyncTC
                    async={{
                      url: "core/vestigingen/search?fields=id as value, systeemNaam as label&per_page=50&sort_field=systeemNaam",
                      searchField: "systeemNaam",
                    }}
                    name={"master_id"}
                    nullable={true}
                    tDefault={"Salesarea order spliting"}
                    tKey={"vestigingen.form.algemeen.label.master_id"}
                  />
                </CardBody>
              </Card>
            </GridLegacy>
            <GridLegacy alignContent={"flex-start"} item xs={12}>
              <Card>
                <CardBody>
                  <GridLegacy item xs={12}>
                    <h3>
                      <TranslationWrapper
                        tDefault={"Bedrijfsgegevens"}
                        tKey={"vestigingen.form.algemeen.headers.bedrijfgegevens"}
                      />
                    </h3>
                  </GridLegacy>
                  <FormTextTC
                    name={"systeemNaam"}
                    rules={{ required: { value: true, message: getTranslation("This field is required!") } }}
                    tDefault={"System name"}
                    tKey={"vestigingen.form.algemeen.label.systeemNaam"}
                  />
                  <FormTextTC name={"naam"} tDefault={"Name"} tKey={"vestigingen.form.algemeen.label.naam"} />
                  <FormTextTC name={"street"} tDefault={"Address"} tKey={"vestigingen.form.overige.label.street"} />
                  <FormTextTC
                    name={"houseNumber"}
                    tDefault={"House number"}
                    tKey={"vestigingen.form.overige.label.houseNumber"}
                  />
                  <FormTextTC
                    name={"postalCode"}
                    tDefault={"Postal code"}
                    tKey={"vestigingen.form.overige.label.postalCode"}
                  />
                  <FormTextTC name={"city"} tDefault={"City"} tKey={"vestigingen.form.overige.label.city"} />
                  <FormSelectTC
                    name={"country"}
                    onChange={changeHandlerCountry}
                    options={countryOptions}
                    tDefault={"Country"}
                    tKey={"vestigingen.form.overige.label.country"}
                    value={valueCountry}
                  />
                  <FormTextTC
                    name={"btwnummer"}
                    tDefault={"VAT number"}
                    tKey={"vestigingen.form.overige.label.btwnummer"}
                  />
                  <FormTextTC name={"kvk"} tDefault={"COC number"} tKey={"vestigingen.form.overige.label.kvk"} />
                  <FormTextTC name={"lat"} tDefault={"Latitude"} tKey={"vestigingen.form.overige.label.lat"} />
                  <FormTextTC name={"lng"} tDefault={"Longitude"} tKey={"vestigingen.form.overige.label.lng"} />
                  <FormFieldTwoColumns name={"latlong_search"}>
                    <Button color={"primary"} onClick={search} size={"sm"}>
                      <TranslationWrapper tDefault={"Opzoeken"} tKey={"vestigingen.form.algemeen.button.zoeken"} />
                    </Button>
                  </FormFieldTwoColumns>
                  <FormTextTC
                    name={"extra_settings__avPdf"}
                    tDefault={"Custom AV PDF"}
                    tKey={"vestigingen.form.algemeen.label.avPdf"}
                  />
                  <FormTextTC
                    name={"extra_settings__pvPdf"}
                    tDefault={"Custom PV PDF"}
                    tKey={"vestigingen.form.algemeen.label.pvPdf"}
                  />
                  <FormSwitchTC
                    gridSizes={{ column1: { xs: 6 }, column2: { xs: 6 } }}
                    name={"extra_settings__disableTerms"}
                    tDefault={"Disable terms&conditions"}
                    tKey={"vestigingen.form.overige.label.extra_settings__disableTerms"}
                  />
                </CardBody>
              </Card>
            </GridLegacy>
          </GridLegacy>
        </GridLegacy>

        <GridLegacy item lg={6} xs={12}>
          <GridLegacy container spacing={1}>
            <GridLegacy alignItems={"flex-start"} item xs={12}>
              <Card>
                <CardBody>
                  <GridLegacy item xs={12}>
                    <h3>
                      <TranslationWrapper tDefault={"Overige"} tKey={"vestigingen.form.algemeen.headers.overige"} />
                    </h3>
                  </GridLegacy>
                  <FormSwitchTC
                    name={"systemOnline"}
                    tDefault={"Service status"}
                    tKey={"vestigingen.form.algemeen.label.systemOnline"}
                  />
                  <FormSwitchTC
                    name={"systemBlocked"}
                    tDefault={"Systeem geblokkeerd"}
                    tKey={"vestigingen.form.algemeen.label.systemBlocked"}
                  />
                  <FormSelectTC
                    name={"targetPlatform"}
                    options={runningReleaseOptions}
                    tDefault={"Platform"}
                    tKey={"salesarea.targetPlatform"}
                  />
                  <FormSelectTC
                    name={"dispatch_queue"}
                    options={dispatchQueueOptions}
                    tDefault={"Dispatch queue"}
                    tKey={"salesarea.dispatch_queue"}
                  />
                  <FormSwitchTC
                    name={"allowRc"}
                    tDefault={"Allow RC deployment"}
                    tKey={"vestigingen.form.algemeen.label.allowRc"}
                  />
                  <FormTextTC
                    name={"remote_id"}
                    tDefault={"Teamviewer ID"}
                    tKey={"vestigingen.form.overige.label.tvid"}
                  />
                </CardBody>
              </Card>
            </GridLegacy>

            <GridLegacy alignContent={"flex-start"} item xs={12}>
              <Card style={{ marginTop: 0 }}>
                <CardBody>
                  <h3>
                    <TranslationWrapper
                      tDefault={"Contactgegevens"}
                      tKey={"vestigingen.form.algemeen.headers.contactgegevens"}
                    />
                  </h3>

                  <FormTextTC name={"tel"} tDefault={"Telefoon"} tKey={"vestigingen.form.overige.label.tel"} />
                  <FormTextTC
                    name={"email"}
                    tDefault={"E-mail address"}
                    tKey={"vestigingen.form.overige.label.email"}
                  />

                  <FormTextTC
                    name={"extra_settings__www"}
                    tDefault={"Website"}
                    tKey={"vestigingen.form.algemeen.label.www"}
                  />
                  <FormTextTC
                    name={"extra_settings__insta"}
                    tDefault={"Instagram"}
                    tKey={"vestigingen.form.algemeen.label.insta"}
                  />
                  <FormTextTC
                    name={"extra_settings__facebook"}
                    tDefault={"Facebook"}
                    tKey={"vestigingen.form.algemeen.label.facebook"}
                  />

                  <FormTextTC
                    name={"gaAccount"}
                    tDefault={"Google Analytics"}
                    tKey={"vestigingen.form.algemeen.label.gaAccount"}
                  />
                  <FormTextTC name={"fb"} tDefault={"Facebook Pixel"} tKey={"vestigingen.form.algemeen.label.fb"} />
                </CardBody>
              </Card>
            </GridLegacy>
          </GridLegacy>
        </GridLegacy>
      </GridLegacy>
    </div>
  );
}

export default Algemeen;
